/**
 * Subdomain → tenant slug extraction.
 *
 * Mirrors the backend's `extract_subdomain` logic in
 * apps/common/middleware/tenant.py. If you change one, change the other.
 */

const RESERVED_SUBDOMAINS = new Set([
  "www",
  "api",
  "admin",
  "app",
  "staging",
  "dev",
  "support",
]);

/**
 * Returns the current tenant slug, or null if on the root domain or a
 * reserved subdomain.
 *
 * Examples:
 *   acme.tina.kraetd.com  → "acme"
 *   tina.kraetd.com       → null
 *   acme.localhost:5173            → "acme"
 *   localhost:5173                 → null
 *   admin.tina.kraetd.com → null (reserved)
 */
export function getTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  return extractSubdomain(host);
}

export function extractSubdomain(host: string): string | null {
  // *.localhost shortcut for local dev
  if (host === "localhost") return null;
  if (host.endsWith(".localhost")) {
    const slug = host.slice(0, -".localhost".length);
    return RESERVED_SUBDOMAINS.has(slug) ? null : slug;
  }

  const baseHost = (
    (import.meta.env.VITE_APP_HOST as string | undefined) ?? "tina.kraetd.com"
  ).toLowerCase();

  if (host === baseHost) return null;

  const suffix = "." + baseHost;
  if (host.endsWith(suffix)) {
    const sub = host.slice(0, -suffix.length);
    // Handle multi-level: *.staging.tina.kraetd.com → take last segment
    const slug = sub.includes(".") ? sub.split(".").pop()! : sub;
    return RESERVED_SUBDOMAINS.has(slug) ? null : slug;
  }

  return null;
}

export function isReservedSubdomain(slug: string): boolean {
  return RESERVED_SUBDOMAINS.has(slug);
}

/**
 * Build a URL on a different tenant subdomain (for "Switch tenant" UX).
 * Preserves protocol + port + pathname.
 */
export function urlForTenant(slug: string): string {
  if (typeof window === "undefined") return "";
  const { protocol, port, pathname } = window.location;
  const baseHost = (
    (import.meta.env.VITE_APP_HOST as string | undefined) ?? "tina.kraetd.com"
  ).toLowerCase();
  const host = baseHost === "localhost" ? `${slug}.localhost` : `${slug}.${baseHost}`;
  const portStr = port ? `:${port}` : "";
  return `${protocol}//${host}${portStr}${pathname}`;
}
